/* The published legal documents, as structured content — STORY-048.
 *
 * The wire shape is a list of BLOCKS, not a string of HTML, and that is the
 * decision this file exists to fix.
 *
 * `frontend-react.md` bans `dangerouslySetInnerHTML` outright, so a document
 * cannot arrive as rendered HTML — there would be no legal way to put it on
 * screen. Sending Markdown instead would only move the parser into every
 * client and give the web and the native apps two chances to render the same
 * clause differently. So the API parses once and sends elements, and each
 * client maps a block to whatever it draws with. That is also what makes the
 * mobile app free later: a heading is a heading whether it becomes an `<h2>`
 * or a `<Text>`.
 */
import { z } from 'zod';
/**
 * The documents that are served. Not free text — the route parameter is one of
 * these, so an unknown slug is a 404 from the schema rather than a file lookup
 * with a user-supplied name in it.
 */
export const LEGAL_DOCUMENTS = ['terms', 'privacy', 'dpa'];
/**
 * Inline runs inside a block.
 *
 * Deliberately small. Bold, code and links are what these documents actually
 * use; anything else renders as text rather than being silently dropped, which
 * is the safer failure for a contract.
 */
export const inlineSchema = z.object({
    text: z.string(),
    bold: z.boolean().optional(),
    italic: z.boolean().optional(),
    code: z.boolean().optional(),
    /** Present only on a link run. */
    href: z.string().optional(),
});
const inlines = z.array(inlineSchema);
export const legalBlockSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('heading'), level: z.number().int().min(1).max(4), content: inlines }),
    z.object({ kind: z.literal('paragraph'), content: inlines }),
    z.object({ kind: z.literal('quote'), content: z.array(inlines) }),
    z.object({ kind: z.literal('list'), ordered: z.boolean(), items: z.array(inlines) }),
    z.object({
        kind: z.literal('table'),
        headers: z.array(inlines),
        rows: z.array(z.array(inlines)),
    }),
    z.object({ kind: z.literal('rule') }),
]);
/**
 * Which documents are published — the cheap question.
 *
 * The signup screen needs to know whether to promise acceptance or say the
 * documents are still being finalised. Answering that by fetching both
 * documents would pull several thousand words to choose between two sentences,
 * so the index is its own tiny endpoint.
 */
export const legalIndexResponseSchema = z.object({
    published: z.array(z.enum(LEGAL_DOCUMENTS)),
});
export const legalDocumentResponseSchema = z.object({
    slug: z.enum(LEGAL_DOCUMENTS),
    title: z.string(),
    /**
     * The exact string recorded in `user_agreements` when this document is
     * accepted, so a row can be traced to the text that was on screen.
     */
    version: z.string(),
    /** ISO date the version took effect. */
    effectiveDate: z.string(),
    blocks: z.array(legalBlockSchema),
});
//# sourceMappingURL=legal.js.map